/**
 * Verification Item Table Component
 *
 * Displays verification items grouped by unit in a table format
 * matching the Mazak 設計検証項目一覧 layout.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { TableBody, TableCell, TableRow } from '@/components/ui/table';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import {
  VerificationSession,
  VerificationUnit,
  VerificationItem,
} from '../../types/verification';
import VerificationItemTableHeader from './item-table/VerificationItemTableHeader';
import VerificationItemRow from './item-table/VerificationItemRow';
import VerificationUnitEmptyRow from './item-table/VerificationUnitEmptyRow';
import { TOTAL_COLUMNS, groupItemsBySpec } from './item-table/itemTableLayout';

interface VerificationItemTableProps {
  session: VerificationSession;
  onAddUnit?: () => void;
  onEditUnit?: (unit: VerificationUnit) => void;
  onDeleteUnit?: (unitId: string) => void;
  onAddItem?: (unitId: string) => void;
  onEditItem?: (item: VerificationItem) => void;
  onDeleteItem?: (itemId: string) => void;
}

const VerificationItemTable: React.FC<VerificationItemTableProps> = ({
  session,
  onAddUnit,
  onEditUnit,
  onDeleteUnit,
  onAddItem,
  onEditItem,
  onDeleteItem,
}) => {
  const { t } = useTranslation();
  const totalItems = session.units.reduce((sum, u) => sum + u.items.length, 0);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
          <span className="revy-figure">{session.units.length}</span> {t('verification.units')}
          {' / '}
          <span className="revy-figure">{totalItems}</span> {t('verification.items')}
        </p>
        {onAddUnit && (
          <TooltipIconButton
            label={t('verification.addUnit')}
            icon={<Plus strokeWidth={1.8} />}
            size="icon-sm"
            className="text-primary"
            onClick={onAddUnit}
          />
        )}
      </div>

      {/*
        The scroll container has to be this element and no other: ui/table's
        <Table> wraps the table in its own `overflow-auto` div, which would
        become the scrollport and leave the sticky header stuck to a box that
        never scrolls vertically. `border-separate` is what MUI's stickyHeader
        switches to as well — with collapsed borders the sticky cells lose theirs.
      */}
      <div className="max-h-[calc(100vh-280px)] overflow-auto rounded-xl border bg-card">
        <table className="w-full min-w-[1200px] caption-bottom border-separate border-spacing-0 text-sm">
          <VerificationItemTableHeader />
          <TableBody>
            {session.units.length === 0 && (
              <TableRow>
                <TableCell colSpan={TOTAL_COLUMNS} className="py-8 text-center">
                  <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
                    {t('verification.noUnits')}
                  </p>
                </TableCell>
              </TableRow>
            )}
            {session.units.map((unit) => {
              const items = unit.items;
              if (items.length === 0) {
                return (
                  <VerificationUnitEmptyRow
                    key={unit.id}
                    unit={unit}
                    onAddItem={onAddItem}
                    onEditUnit={onEditUnit}
                    onDeleteUnit={onDeleteUnit}
                  />
                );
              }

              return groupItemsBySpec(items).map((group, groupIdx) =>
                group.items.map((item, itemIdx) => (
                  <VerificationItemRow
                    key={item.id}
                    unit={unit}
                    item={item}
                    spec={group.spec}
                    isFirstInUnit={groupIdx === 0 && itemIdx === 0}
                    unitRowSpan={items.length}
                    isFirstInGroup={itemIdx === 0}
                    specRowSpan={group.items.length}
                    onAddItem={onAddItem}
                    onEditUnit={onEditUnit}
                    onDeleteUnit={onDeleteUnit}
                    onEditItem={onEditItem}
                    onDeleteItem={onDeleteItem}
                  />
                )),
              );
            })}
          </TableBody>
        </table>
      </div>
    </div>
  );
};

export default VerificationItemTable;
